import { useMemo } from 'react'
import { TablaDatos, ayudanteColumnas } from '../../componentes/TablaDatos'
import type {
  FichasElegidas,
  FilaAgendamiento,
  LineasAnadidas,
  PreciosEditados,
} from './filas'
import { lineasDe, motivoNoPublicable, partnerElegido } from './filas'
import { formatearEuros } from './formato'
import { InsigniaMotivo } from './insignias'
import type { Historial } from './precios'
import { hayDiferencia, compararPrecios, totalAnterior, totalBase } from './precios'
import { FichaResuelta, SelectorFicha } from './SelectorFicha'

/**
 * La revision del modo masivo: una fila por linea pegada, se haya entendido o
 * no. Las que no se entendieron salen igual, marcadas, porque una lista que
 * esconde sus errores hace publicar diecinueve de veinte sin enterarse.
 *
 * Los ambiguos se resuelven **aqui dentro**, en su propia fila y con la
 * direccion de cada candidata a la vista. Sacar esa decision a un dialogo
 * obligaria a abrirlo y cerrarlo por cada uno perdiendo de vista la lista.
 */

const ayudante = ayudanteColumnas<FilaAgendamiento>()

interface Props {
  filas: FilaAgendamiento[]
  fichas: FichasElegidas
  precios: PreciosEditados
  /** Los productos anadidos a mano, por fila: cuentan para el total y para si se publica. */
  anadidas: LineasAnadidas
  historiales: Map<string, Historial>
  seleccionada: string | null
  onSeleccionar: (clave: string) => void
  onElegirFicha: (clave: string, id: number | null) => void
  /**
   * De donde vienen las filas. A la revision se llega marcando casillas en la
   * tabla o pegando una lista, y llamar "resolucion de la lista" a lo que salio
   * de la tabla mandaria a buscar una lista que nadie pego.
   */
  etiqueta?: string
}

function historialDe(fila: FilaAgendamiento, historiales: Map<string, Historial>) {
  const id = fila.resultado?.cliente?.contact_id
  return id ? historiales.get(id) : undefined
}

/** El total anterior y si difiere del que se va a publicar. */
function comparacion(
  fila: FilaAgendamiento,
  precios: PreciosEditados,
  anadidas: LineasAnadidas,
  historiales: Map<string, Historial>,
) {
  const historial = historialDe(fila, historiales)
  const lineas = lineasDe(fila, precios, anadidas)
  return {
    anterior: totalAnterior(historial),
    difiere: hayDiferencia(compararPrecios(lineas, historial)),
  }
}

function CeldaFicha({
  fila,
  fichas,
  onElegirFicha,
}: Pick<Props, 'fichas' | 'onElegirFicha'> & { fila: FilaAgendamiento }) {
  const resultado = fila.resultado
  if (!resultado) return <span className="text-tinta-apagada">-</span>

  const candidatos = resultado.candidatos ?? []
  if (candidatos.length > 0) {
    return (
      <SelectorFicha
        cliente={resultado.cliente?.nombre ?? fila.nif}
        candidatos={candidatos}
        elegida={partnerElegido(fila, fichas)}
        onElegir={(id) => onElegirFicha(fila.clave, id)}
      />
    )
  }
  if (resultado.ficha) return <FichaResuelta ficha={resultado.ficha} />
  return <span className="text-tinta-apagada">Sin ficha</span>
}

function CeldaUltimaVez({ anterior, difiere }: { anterior: number | null; difiere: boolean }) {
  if (anterior === null) return <span className="text-tinta-apagada">Sin dato</span>
  return (
    <span className={difiere ? 'font-semibold text-atencion-tinta' : undefined}>
      {formatearEuros(anterior)}
      {difiere ? ' (distinto)' : ''}
    </span>
  )
}

function CeldaEstado({
  fila,
  fichas,
  anadidas,
}: {
  fila: FilaAgendamiento
  fichas: FichasElegidas
  anadidas: LineasAnadidas
}) {
  const motivo = motivoNoPublicable(fila, fichas, anadidas)
  return (
    <div className="flex flex-col gap-1">
      {fila.resultado ? (
        <InsigniaMotivo motivo={fila.resultado.motivo} />
      ) : (
        <span className="inline-flex rounded-full bg-critico-fondo px-2 py-0.5 text-xs font-semibold text-critico-tinta ring-1 ring-critico-anillo">
          No se entiende
        </span>
      )}
      {motivo ? <span className="max-w-64 text-xs text-tinta-apagada">{motivo}</span> : null}
    </div>
  )
}

export function TablaResolucion({
  filas,
  fichas,
  precios,
  anadidas,
  historiales,
  seleccionada,
  onSeleccionar,
  onElegirFicha,
  etiqueta = 'Resolucion de la lista',
}: Props) {
  const columnas = useMemo(
    () =>
      ayudante.columns([
        ayudante.accessor((fila) => fila.numero ?? 0, { id: 'numero', header: 'Linea' }),
        ayudante.accessor('nif', { header: 'NIF' }),
        ayudante.accessor((fila) => fila.resultado?.cliente?.nombre ?? '', {
          id: 'cliente',
          header: 'Cliente',
          cell: (info) => (
            <span className={info.getValue() ? 'text-tinta' : 'text-tinta-apagada'}>
              {info.getValue() || info.row.original.texto || 'Sin cliente'}
            </span>
          ),
        }),
        ayudante.accessor(
          (fila) => {
            const id = partnerElegido(fila, fichas)
            return id === null ? '' : String(id)
          },
          {
            id: 'ficha',
            header: 'Ficha de Odoo',
            cell: (info) => (
              <CeldaFicha fila={info.row.original} fichas={fichas} onElegirFicha={onElegirFicha} />
            ),
          },
        ),
        ayudante.accessor((fila) => fila.resultado?.pedido?.pedido_txt ?? '', {
          id: 'pedido',
          header: 'Pedido',
          cell: (info) => info.getValue() || '-',
        }),
        ayudante.accessor((fila) => totalBase(lineasDe(fila, precios, anadidas)), {
          id: 'catalogo',
          header: 'Total catalogo',
          // La celda **recalcula** en vez de pintar `info.getValue()`. El valor
          // del accessor se cachea por fila y solo se tira al cambiar los datos,
          // asi que un precio corregido o un producto anadido -que no tocan
          // `filas`- dejaban esta columna diciendo el total viejo mientras el
          // detalle de abajo ya decia el nuevo. El accessor se queda para
          // ordenar; lo que se lee sale de aqui.
          cell: (info) =>
            info.row.original.resultado?.pedido || (anadidas[info.row.original.clave] ?? []).length
              ? formatearEuros(totalBase(lineasDe(info.row.original, precios, anadidas)))
              : '-',
        }),
        ayudante.accessor((fila) => comparacion(fila, precios, anadidas, historiales).anterior ?? -1, {
          id: 'anterior',
          header: 'Ultima vez',
          cell: (info) => (
            <CeldaUltimaVez {...comparacion(info.row.original, precios, anadidas, historiales)} />
          ),
        }),
        ayudante.accessor((fila) => fila.resultado?.motivo ?? 'ilegible', {
          id: 'estado',
          header: 'Estado',
          cell: (info) => (
            <CeldaEstado fila={info.row.original} fichas={fichas} anadidas={anadidas} />
          ),
        }),
      ]),
    [fichas, precios, anadidas, historiales, onElegirFicha],
  )

  return (
    <>
      {/* Tabla en escritorio. Ocho columnas a 375 px no se leen. */}
      <div className="hidden lg:block">
        <TablaDatos
          datos={filas}
          columnas={columnas}
          estadoVacio={null}
          etiqueta={etiqueta}
          obtenerId={(fila) => fila.clave}
          seleccionadoId={seleccionada}
          onSeleccionar={(fila) => onSeleccionar(fila.clave)}
        />
      </div>

      {/* Tarjetas apiladas en movil, con lo mismo que dice la tabla. */}
      <ul aria-label="Resolucion en tarjetas" className="flex flex-col gap-2 lg:hidden">
        {filas.map((fila) => {
          const { anterior, difiere } = comparacion(fila, precios, anadidas, historiales)
          return (
            <li
              key={fila.clave}
              className="flex flex-col gap-2 rounded-xl border border-borde bg-superficie px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-tinta">
                  {fila.resultado?.cliente?.nombre || fila.texto || fila.nif}
                </span>
                <CeldaEstado fila={fila} fichas={fichas} anadidas={anadidas} />
              </div>
              <p className="text-xs text-tinta-apagada">{fila.nif}</p>
              <CeldaFicha fila={fila} fichas={fichas} onElegirFicha={onElegirFicha} />
              {fila.resultado?.pedido ? (
                <div className="flex flex-col gap-1 text-sm text-tinta-2">
                  <span>{fila.resultado.pedido.pedido_txt}</span>
                  <span>
                    Catalogo {formatearEuros(totalBase(lineasDe(fila, precios, anadidas)))} / ultima vez{' '}
                    <CeldaUltimaVez anterior={anterior} difiere={difiere} />
                  </span>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => onSeleccionar(fila.clave)}
                className="self-start rounded-lg border border-borde-fuerte px-3 py-1.5 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
              >
                {seleccionada === fila.clave ? 'Ocultar las lineas' : 'Ver las lineas'}
              </button>
            </li>
          )
        })}
      </ul>
    </>
  )
}
